import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmployerService } from '@/services/employer.service';
import { Employer } from '@/models/employer.model';
import { Button, Card, EmptyState, LoadingSpinner, SectionHeader, Badge, FormInput, FormSelect, FormTextarea } from '@/components/common';
import { Building2, Plus, Search, Users, CheckCircle2, XCircle, DollarSign, Filter, MoreVertical, Eye, Trash2 } from 'lucide-react';

export const EmployerManagement: React.FC = () => {
  const navigate = useNavigate();
  const [employers, setEmployers] = useState<Employer[]>([]);
  const [filteredEmployers, setFilteredEmployers] = useState<Employer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  useEffect(() => {
    const unsubscribe = EmployerService.subscribeToEmployers((data) => {
      setEmployers(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let filtered = [...employers];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(e =>
        e.name.toLowerCase().includes(query) ||
        e.description?.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (filterStatus === 'active') {
      filtered = filtered.filter(e => e.isActive);
    } else if (filterStatus === 'inactive') {
      filtered = filtered.filter(e => !e.isActive);
    }

    setFilteredEmployers(filtered);
  }, [employers, searchQuery, filterStatus]);

  const handleToggleStatus = async (employer: Employer) => {
    try {
      await EmployerService.toggleEmployerStatus(employer.id, employer.isActive);
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update employer status');
    }
  };

  const handleDelete = async (employer: Employer) => {
    if (!confirm(`Are you sure you want to delete ${employer.name}? This action cannot be undone.`)) {
      return;
    }
    try {
      await EmployerService.deleteEmployer(employer.id);
    } catch (error) {
      console.error('Error deleting employer:', error);
      alert('Failed to delete employer');
    }
  };

  // Calculate stats
  const stats = {
    total: employers.length,
    active: employers.filter(e => e.isActive).length,
    inactive: employers.filter(e => !e.isActive).length,
    totalMembers: employers.reduce((sum, e) => sum + e.totalMembers, 0),
  };

  if (loading && employers.length === 0) {
    return <LoadingSpinner text="Loading employers..." fullScreen />;
  }

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SectionHeader
        icon={Building2}
        title="Employer Management"
        description="Manage employer accounts and settings"
        action={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => setShowCreateDialog(true)}
          >
            Add Employer
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card hover>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Total Employers</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>
          <Card hover>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Active</p>
                <p className="text-3xl font-bold text-green-600">{stats.active}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>
          <Card hover>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Inactive</p>
                <p className="text-3xl font-bold text-gray-600">{stats.inactive}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                <XCircle className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </Card>
          <Card hover>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Total Members</p>
                <p className="text-3xl font-bold text-purple-600">{stats.totalMembers}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Search Employers"
            icon={Search}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or description..."
          />
          <FormSelect
            label="Status"
            options={statusOptions}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
          />
        </div>
      </Card>

      {/* Employer List */}
      <Card padding="none">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Employers ({filteredEmployers.length})
          </h3>
        </div>
        {filteredEmployers.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="No employers found"
            description={
              searchQuery || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by creating a new employer'
            }
            action={
              !searchQuery && filterStatus === 'all' && (
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() => setShowCreateDialog(true)}
                >
                  Add Employer
                </Button>
              )
            }
          />
            ) : (
            <div className="divide-y divide-gray-200">
              {filteredEmployers.map((employer) => (
                <EmployerCard
                  key={employer.id}
                  employer={employer}
                  onView={() => navigate(`/employers/${employer.id}`)}
                  onToggleStatus={() => handleToggleStatus(employer)}
                  onDelete={() => handleDelete(employer)}
                />
              ))}
            </div>
          )}
      </Card>

      {/* Create Dialog */}
      {showCreateDialog && (
        <CreateEmployerDialog
          onClose={() => setShowCreateDialog(false)}
          onSuccess={() => {
            setShowCreateDialog(false);
          }}
        />
      )}
    </div>
  );
};


interface EmployerCardProps {
  employer: Employer;
  onView: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}

const EmployerCard: React.FC<EmployerCardProps> = ({
  employer,
  onView,
  onToggleStatus,
  onDelete,
}) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="px-6 py-4 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent transition-all cursor-pointer group" onClick={onView}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
            employer.isActive 
              ? 'bg-gradient-to-br from-green-100 to-green-200' 
              : 'bg-gradient-to-br from-gray-100 to-gray-200'
          }`}>
            <Building2 className={`w-6 h-6 ${employer.isActive ? 'text-green-600' : 'text-gray-600'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{employer.name}</h4>
              <Badge variant={employer.isActive ? "success" : "gray"} size="sm">
                {employer.isActive ? 'Active' : 'Inactive'}
              </Badge>
              {employer.paymentMethodVerified && (
                <Badge variant="info" size="sm">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Payment Verified
                </Badge>
              )}
            </div>
            {employer.description && (
              <p className="text-sm text-gray-600 line-clamp-2 mb-3">{employer.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {employer.totalMembers} members
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {employer.adminUserIds.length} admin{employer.adminUserIds.length !== 1 ? 's' : ''}
              </span>
              {employer.monthlyBudgetLimit && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  ${employer.monthlyBudgetLimit.toLocaleString()}/mo
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
          <Button
            variant={employer.isActive ? "danger" : "success"}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onToggleStatus();
            }}
            gradient={false}
          >
            {employer.isActive ? 'Deactivate' : 'Activate'}
          </Button>
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              icon={MoreVertical}
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
            />
            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                  }}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-20 border border-gray-200">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      onView();
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    View Details
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      onDelete();
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface CreateEmployerDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateEmployerDialog: React.FC<CreateEmployerDialogProps> = ({ onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Please enter employer name');
      return;
    }

    setLoading(true);
    try {
      await EmployerService.createEmployer({
        name: name.trim(),
        description: description.trim() || undefined,
        isActive: true,
      });
      onSuccess();
    } catch (error: any) {
      console.error('Error creating employer:', error);
      setError(error?.message || 'Failed to create employer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <Card className="relative max-w-md w-full z-10">
          <div className="flex items-center gap-2 mb-6">
            <Building2 className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Create New Employer</h2>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 rounded-r-lg p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <FormInput
              label="Employer Name"
              icon={Building2}
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter employer name"
            />
            <FormTextarea
              label="Description"
              icon={Building2}
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter employer description (optional)"
            />
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                icon={Plus}
                isLoading={loading}
                iconPosition="right"
              >
                Create Employer
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

